import React, { useEffect, useRef, useState } from 'react';
import { CheckoutOpenOptions, initializePaddle, Paddle } from '@paddle/paddle-js';
import { Dialog, ElevatedButton, TextButton } from '@producer/ui-kit';

import { Space } from '../../../app/entities';
import { api } from '../../../api';
import { globalConfig } from '../../../configuration/config';
import { PageLoader } from '../../../app/components/page-loader';
import { currencyFormatter } from '../../../lib/utils/currency-formatter';

import styles from './purchase-credits-modal.module.scss';
import { NumberInput } from '../../../components/inputs/number.input';

interface PurchaseCreditsModalProps {
  space: Space;
  onClose: () => void;
  onCancel: () => void;
}

export const PurchaseCreditsModal: React.FC<PurchaseCreditsModalProps> = ({
  space,
  onClose,
  onCancel,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPaddle, setIsLoadingPaddle] = useState(true);
  const [creditsQuantity, setCreditsQuantity] = useState<number>(1_000);
  const [currency, setCurrency] = useState<string>('USD');
  const [price, setPrice] = useState<number>(1_000);

  const paddleInstance = useRef<Paddle>(null);

  const paddleToken = globalConfig.config.paddleToken;

  const paddlePriceItem = {
    quantity: creditsQuantity,
    priceId: globalConfig.config.paddleCreditPrice,
  };

  const handleBlur = () => {
    if (creditsQuantity < 1_000) {
      setCreditsQuantity(1_000);
    } else if (creditsQuantity > 10_000) {
      setCreditsQuantity(10_000);
    } else {
      setCreditsQuantity(creditsQuantity);
    }
  };

  useEffect(() => {
    initializePaddle({
      environment: globalConfig.config.environment === 'production' ? 'production' : 'sandbox',
      token: paddleToken!,
    })
      .then((paddle: Paddle | undefined) => {
        if (!paddle) return;

        paddleInstance.current = paddle;

        return paddle.PricePreview({
          items: [paddlePriceItem],
        });
      })
      .then((result) => {
        if (!result) return;

        const items = result.data.details.lineItems;

        if (items && items.length > 0) {
          const item = items[0];
          setCurrency(item.price.unitPrice.currencyCode);
          setPrice(parseFloat(item.price.unitPrice.amount));
        }

        setIsLoadingPaddle(false);
      })
      .catch((error) => {
        console.error(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      await api
        .post(`spaces/${space._id}/credits`, {
          quantity: creditsQuantity,
        })
        .then(({ data }) => {
          const paddle = paddleInstance.current;

          const checkoutInput: CheckoutOpenOptions = {
            transactionId: data.transactionId,
            settings: {
              theme: 'dark',
              locale: 'en',
              allowLogout: false,
            },
          };

          if (data.customerId) {
            checkoutInput.customer = {
              id: data.customerId,
            };
          }

          paddle.Checkout.open(checkoutInput);
        });

      setIsLoading(false);
      onClose();
    } catch (e) {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      title={'Buy credits'}
      onClose={onClose}
      actions={[
        <TextButton key="cancel" text="Cancel" onClick={onCancel} />,
        <ElevatedButton
          key="buy"
          text="Buy"
          form="purchase-credits-form"
          isLoading={isLoading}
          onClick={onSubmit}
        />,
      ]}
    >
      {isLoadingPaddle ? (
        <div className={styles.LoaderContainer}>
          <PageLoader />
        </div>
      ) : (
        <div className={styles.container}>
          <label>Quantity</label>

          <div className={styles.FormGroup}>
            <NumberInput
              type="number"
              value={creditsQuantity}
              onChange={(e) => setCreditsQuantity(e.target.valueAsNumber)}
              onBlur={handleBlur}
              min={1000}
              max={10_000}
              step={1_000}
            />

            <p>{currencyFormatter(creditsQuantity * (price / 100), currency)}</p>
          </div>

          <label>Equals to {creditsQuantity / 5} generated storyboard images.</label>
        </div>
      )}
    </Dialog>
  );
};
